























































































































































































































































import Dropdown, { DropdownItem } from '@/components/Dropdown.vue';
import Icon from '@/components/Icon.vue';
import ModalAttachQueue from '@/components/agent/ModalAttachQueue.vue';
import ModalUpdateAgent from '@/components/agent/ModalUpdateAgent.vue';
import Call from '@/components/queue/Call.vue';
import Tooltip from '@/components/Tooltip.vue';
import {
    AgentStatus,
    AgentCustomStatus,
    AgentConduct,
} from '@/utils/agent';
import { TierState } from '@/utils/tier/state';
import { VisionMode } from '@/utils/vision';
import {
    Vue,
    Component,
    Prop,
    Watch,
} from 'vue-property-decorator';
import { Agent as StoredAgent, BridgeAgent } from '@/store/modules/agent';
import { LocaleMessages } from 'vue-i18n';
import Chronometer from '@/atomics/Chronometer.vue';

interface Tier {
    agent: string;
    queue: string;
    state: string;
}

interface QueueCallWithRenderInfo {
    call: BridgeAgent;
    renderKey: string;
    chronometerId: string;
}

@Component({
    components: {
        Dropdown,
        Icon,
        ModalAttachQueue,
        Tooltip,
        Call,
        ModalUpdateAgent,
        Chronometer,
    },
})
export default class Agent extends Vue {
    @Prop(Boolean) readonly moreDetail!: boolean;

    @Prop(String) readonly name!: string;

    @Prop(Boolean) readonly connected!: boolean;

    @Prop(Array) readonly queues!: Array<string>;

    @Prop({ default: VisionMode.Minimized }) readonly visionMode!: VisionMode;

    @Prop(Number) readonly loadedTime!: number;

    @Prop(Boolean) readonly idleTimeHidden!: boolean;

    @Prop(Boolean) readonly show!: boolean;

    @Prop({ type: Boolean, default: false }) readonly disableActions!: boolean;

    tierState = new TierState();

    ringing = false;

    isVoicebotCallsExpanded = false;

    get isDisconnected(): boolean {
        return !this.connected || !this.agent.connected;
    }

    get agentAsTierInQueue(): Tier {
        return {
            agent: this.agent?.name || '',
            queue: '',
            state: 'available',
        };
    }

    get agentName(): string {
        return this.$store.getters.agentName(this.agent.name);
    }

    get forcedIconClass(): string {
        if (this.isDisconnected) {
            return this.agentCustomStatus?.statusIconClass();
        }
        if (this.isInCallFromTimer || this.hasActiveCallStates) {
            return 'text-red-500';
        }
        return this.agent?.doNotDisturb ? 'text-blue-500' : this.agentCustomStatus?.statusIconClass();
    }

    get rootBgClass(): string {
        if (this.isInCallFromTimer || this.hasActiveCallStates) {
            return 'bg-red-50';
        }
        if (this.isDisconnected) {
            return 'bg-gray-50';
        }
        if (this.agent?.doNotDisturb) {
            return 'bg-blue-50';
        }
        if (this.agent && AgentStatus.isAvailableOnDemand(this.agent.status)) {
            return 'bg-orange-50';
        }
        const status = this.agentCustomStatus?.status();
        if (status === 'available') {
            return 'bg-green-50';
        }
        if (status === 'busy') {
            return 'bg-yellow-50';
        }
        return 'bg-gray-50';
    }

    get isInCallFromTimer(): boolean {
        if (this.isDisconnected) {
            return false;
        }
        return this.agent?.state === 'in_queue_call';
    }

    get hasActiveCallStates(): boolean {
        if (this.isDisconnected) {
            return false;
        }
        return (this.getCallStates?.length || 0) > 0 || (this.getQueueCalls?.length || 0) > 0;
    }

    get shouldShowDndStatus(): boolean {
        return Boolean(!this.isDisconnected && this.agent?.doNotDisturb && this.agent?.customStatusMessage);
    }

    get isPaused(): boolean {
        return this.agent?.customStatusMessage?.toLowerCase().includes('paused')
            || (this.agent?.doNotDisturb && !this.isDisconnected);
    }

    dropdownItems: Array<DropdownItem> = [
        {
            name: this.$t('assign_queue'),
            action: () => { (this.$refs.modalAttachQueue as ModalAttachQueue).toogleModal(); },
        },
        {
            name: this.$t('edit_agent'),
            action: () => { (this.$refs.modalUpdateAgent as ModalUpdateAgent).toogleModal(); },
        },
        {
            name: this.$t('hangup_call'),
            action: () => {
                const call = this.$store.getters.getFirstCallIdByAgent(this.name);
                if (call !== '') {
                    this.$store.dispatch('hangupCall', { name: this.name, call });
                }
            },
        },
    ]

    get nameAgent(): string {
        return this.$store.getters.agentName(this.name);
    }

    get nameAgentExtension(): string {
        return this.$store.getters.agentExtensionName(this.name);
    }

    get isInactive(): boolean {
        return this.getQueueCalls.length <= 0 && this.getCallStates.length <= 0;
    }

    get agentCustomStatus(): AgentCustomStatus {
        const agentConduct: AgentConduct = {
            connected: !this.isDisconnected && !this.agent.isLoading,
            active: !this.isInactive,
        };
        return AgentCustomStatus.fromPanel(
            this.agent.status,
            this.agent.state,
            agentConduct,
            this.agent.previousState,
        );
    }

    get getQueueCalls() {
        if (this.isDisconnected) {
            return [];
        }
        return Array.from(this.agent.queueCalls.values());
    }

    get queueCallsToRender(): Array<QueueCallWithRenderInfo> {
        if (this.isDisconnected) {
            return [];
        }

        const queueCallEntries = Array.from(this.agent.queueCalls.entries()) as Array<[string, BridgeAgent]>;

        return queueCallEntries.map(([entryKey, call], index) => ({
            call,
            renderKey: this.isVoicebotSpecialCard ? `voicebot-call-${entryKey}` : String(index),
            chronometerId: this.resolveChronometerId(entryKey, call),
        }));
    }

    get getCallStates() {
        if (this.isDisconnected) {
            return [];
        }
        return this.$store.getters.agentCallStates(this.name);
    }

    get isAvailable(): boolean {
        return AgentStatus.isAvailable(this.agent.status);
    }

    get agent(): StoredAgent {
        return this.$store.getters.getAgentByName(this.name) as StoredAgent;
    }

    get isVoicebotAgent(): boolean {
        return this.name.toLowerCase().startsWith('voicebot@');
    }

    get isVoicebotSpecialCard(): boolean {
        return this.isVoicebotAgent && this.disableActions;
    }

    get voicebotCallsCount(): number {
        try {
            return this.queueCallsToRender.length;
        } catch (error) {
            return 0;
        }
    }

    get hasVoicebotActiveCalls(): boolean {
        return this.voicebotCallsCount > 0;
    }

    get shouldRenderStandardCalls(): boolean {
        return !this.isVoicebotSpecialCard;
    }

    get shouldShowVoicebotCallsPanel(): boolean {
        return this.isVoicebotSpecialCard && this.hasVoicebotActiveCalls && this.isVoicebotCallsExpanded;
    }

    get voicebotCardStyle(): Record<string, string> {
        if (!this.isVoicebotSpecialCard) {
            return {};
        }

        return {};
    }

    get customStatusTranslation(): string | LocaleMessages {
        if (this.isDisconnected) {
            return this.$t(this.agentCustomStatus.translation());
        }
        return this.agent.doNotDisturb
            ? this.$t('dnd')
            : this.$t(this.agentCustomStatus.translation());
    }

    get tooltipName(): string {
        if (this.nameAgent !== this.nameAgentExtension) {
            return `(${this.nameAgentExtension}) ${this.nameAgent}`;
        }
        return this.nameAgent;
    }

    calcSecs(secs: number): number {
        const freeTime = (Date.now() - this.loadedTime) / 1000;
        return secs + freeTime;
    }

    resolveChronometerId(entryKey: string, call: BridgeAgent): string {
        if (!this.isVoicebotSpecialCard) {
            return call.call;
        }

        return `voicebot-call-${entryKey}`;
    }

    toggleVoicebotCallsPanel(): void {
        try {
            if (!this.isVoicebotSpecialCard || !this.hasVoicebotActiveCalls) {
                return;
            }

            this.isVoicebotCallsExpanded = !this.isVoicebotCallsExpanded;
        } catch (error) {
            this.isVoicebotCallsExpanded = false;
        }
    }

    closeVoicebotCallsPanel(): void {
        this.isVoicebotCallsExpanded = false;
    }

    @Watch('voicebotCallsCount')
    onVoicebotCallsCountChanged(count: number): void {
        if (count === 0 && this.isVoicebotCallsExpanded) {
            this.isVoicebotCallsExpanded = false;
        }
    }
}
